import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { validateDateBR } from '../lib/helpers';
import {
  Save, 
  ArrowLeft, 
  Calendar,
  DollarSign,
  User,
  Building,
  Briefcase,
  Target,
  TrendingUp,
  Award,
  GraduationCap,
  BarChart3,
  Grid3X3,
  Key,
  Smartphone,
  Loader2,
  Check,
  X
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CurrencyInput } from './ui/currency-input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [activeTab, setActiveTab] = useState(isEditing ? "profile" : "basic");
  const [contractualLoaded, setContractualLoaded] = useState(false);
  const [loadingContractual, setLoadingContractual] = useState(false);
  const [profileData, setProfileData] = useState({
    disc: null,
    performance: null,
    pdi: null,
    ninebox: null,
    lms: null
  });
  
  // Form data
  const [formData, setFormData] = useState({
    empresa: '',
    nome: '',
    filiais: '',
    cargo: '',
    admissao: '',
    regime: '',
    tipo_contrato: '',
    departamento: '',
    situacao: '',
    salario: 0,
    acumulo_funcao: 0,
    vt: 0,
    taxa_vt: 0,
    repasse_vt: 0,
    va: 0,
    vr: 0,
    aux_combustivel: 0,
    hora_extra: 0,
    comissao: 0,
    ajuda_custo: 0,
    auxilio_creche: 0,
    segunda_refeicao: 0,
    pernoite: 0,
    assist_medica: 0,
    odonto: 'Nenhuma',
    seg_vida: 'Nenhuma',
    total_pass: 'Nenhuma',
    med_ocupacional: 'Nenhuma',
    nf_13: 0,
    inss: 0,
    possui_decimo_terceiro: true
  });

  // PIN state
  const [pinData, setPinData] = useState({
    pin: null,
    tem_pin: false,
    loading: false,
    generating: false,
    showDialog: null
  });

  // Options for select fields
  const [options, setOptions] = useState({
    companies: [],
    branches: [],
    positions: [],
    departments: [],
    status: [],
    odontoOptions: [],
    segVidaOptions: [],
    totalPassOptions: [],
    medOcupacionalOptions: []
  });

  useEffect(() => {
    fetchOptions();
    if (isEditing) {
      fetchEmployee();
      loadProfileData();
      fetchPinData();
    }
  }, [id]);

  const loadProfileData = async () => {
    if (!id) return;
    
    try {
      const [discResponse, performanceResponse, pdiResponse, nineboxResponse, lmsResponse] = await Promise.allSettled([
        axios.get(`${API}/disc/assessments`).then(r => r.data.filter(a => a.employee_id === id)),
        axios.get(`${API}/performance/evaluations`).then(r => r.data.filter(e => e.employee_id === id)),
        axios.get(`${API}/pdi/pdis`).then(r => r.data.filter(p => p.employee_id === id)),
        axios.get(`${API}/nine-box/positions`).then(r => r.data.find(p => p.employee_id === id)),
        axios.get(`${API}/lms/my-courses`).catch(() => [])
      ]);

      setProfileData({
        disc: discResponse.status === 'fulfilled' ? discResponse.value : [],
        performance: performanceResponse.status === 'fulfilled' ? performanceResponse.value : [],
        pdi: pdiResponse.status === 'fulfilled' ? pdiResponse.value : [],
        ninebox: nineboxResponse.status === 'fulfilled' ? nineboxResponse.value : null,
        lms: lmsResponse.status === 'fulfilled' ? lmsResponse.value : []
      });
    } catch (error) {
      console.error('Error loading profile data:', error);
    }
  };

  // Helper functions for profile display
  const getDISCColor = (profile) => {
    const colors = {
      'D': 'text-red-600 bg-red-100',
      'I': 'text-yellow-600 bg-yellow-100', 
      'S': 'text-green-600 bg-green-100',
      'C': 'text-blue-600 bg-blue-100'
    };
    return colors[profile] || 'text-gray-600 bg-gray-100';
  };

  const getProfileProgress = (data, type) => {
    if (!data) return 0;
    
    switch (type) {
      case 'disc':
        return data.length > 0 ? 100 : 0;
      case 'performance':
        return data.length > 0 ? Math.min(data.length * 25, 100) : 0;
      case 'pdi':
        if (!data.length) return 0;
        const activePDIs = data.filter(p => p.status === 'active');
        return activePDIs.length > 0 ? 75 : 50;
      case 'ninebox':
        return data && data.position ? 100 : 0;
      case 'lms':
        if (!data || !data.length) return 0;
        const completedCourses = data.filter(c => c.progress_percentage >= 100);
        return Math.min((completedCourses.length / data.length) * 100, 100);
      default:
        return 0;
    }
  };

  // Recalculate whenever salario, vt, or regime changes
  useEffect(() => {
    if (formData.salario !== undefined || formData.vt !== undefined || formData.regime) {
      setFormData(prev => calculateEmployeeFields(prev));
    }
  }, [formData.salario, formData.vt, formData.regime]);

  const fetchPinData = async () => {
    if (!id) return;
    try {
      setPinData(prev => ({ ...prev, loading: true }));
      const r = await axios.get(`${API}/ponto-app/pins`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      const employees = r.data || [];
      const match = employees.find(e => e.employee_id === id);
      setPinData(prev => ({
        ...prev,
        tem_pin: match?.tem_pin || false,
        pin: match?.pin || null,
        loading: false
      }));
    } catch (e) {
      console.error('Erro ao carregar PIN:', e);
      setPinData(prev => ({ ...prev, loading: false }));
    }
  };

  const gerarPin = async () => {
    setPinData(prev => ({ ...prev, generating: true }));
    try {
      const r = await axios.post(`${API}/ponto-app/pins/gerar?employee_id=${id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setPinData(prev => ({ ...prev, showDialog: r.data, tem_pin: true }));
      fetchPinData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar PIN");
    } finally {
      setPinData(prev => ({ ...prev, generating: false }));
    }
  };

  const resetPin = async () => {
    if (!window.confirm("Tem certeza? O PIN atual será substituído.")) return;
    gerarPin();
  };

  const fetchOptions = async () => {
    try {
      setLoadingOptions(true);
      const [departments, status, odontoOptions, segVidaOptions, totalPassOptions, medOcupacionalOptions] = await Promise.all([
        axios.get(`${API}/departments`),
        axios.get(`${API}/status`),
        axios.get(`${API}/odonto-options`),
        axios.get(`${API}/seg-vida-options`),
        axios.get(`${API}/total-pass-options`),
        axios.get(`${API}/med-ocupacional-options`)
      ]);

      setOptions({
        companies: [],
        branches: [],
        positions: [],
        departments: departments.data,
        status: status.data,
        odontoOptions: odontoOptions.data,
        segVidaOptions: segVidaOptions.data,
        totalPassOptions: totalPassOptions.data,
        medOcupacionalOptions: medOcupacionalOptions.data
      });
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
      toast.error('Erro ao carregar opções. Verifique se as configurações estão definidas.');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadContractualData = useCallback(async () => {
    if (contractualLoaded || loadingContractual) return;
    setLoadingContractual(true);
    try {
      const [companiesRes, branchesRes, cargosRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/branches`),
        axios.get(`${API}/position-salaries/cargos`)
      ]);
      setOptions(prev => ({
        ...prev,
        companies: Array.isArray(companiesRes.data) ? companiesRes.data : [],
        branches: Array.isArray(branchesRes.data) ? branchesRes.data : [],
        positions: Array.isArray(cargosRes.data) ? cargosRes.data : []
      }));
      setContractualLoaded(true);
    } catch (error) {
      console.error('Erro ao carregar dados contratuais:', error);
      toast.error('Erro ao carregar empresas, filiais e cargos');
    } finally {
      setLoadingContractual(false);
    }
  }, [contractualLoaded, loadingContractual]);

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/employees/${id}`);
      // Recalculate fields to ensure consistency
      const calculatedData = calculateEmployeeFields(response.data);
      setFormData(calculatedData);
    } catch (error) {
      console.error('Erro ao carregar funcionário:', error);
      toast.error('Erro ao carregar dados do funcionário');
      navigate('/funcionarios');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Mark manual edit for nf_13 field
      if (field === 'nf_13') {
        newData.nf_13_manual_edit = true;
      }
      
      // Reset manual edit flag when checkbox toggles
      if (field === 'possui_decimo_terceiro') {
        newData.nf_13_manual_edit = false;
      }
      
      // Regra específica: Se VT = 0, então VT Custo e Desconto VT também devem ser 0
      if (field === 'vt' && (value === 0 || value === '0' || !value)) {
        newData.vt_custo = (parseFloat(newData.taxa_vt) || 0) + (parseFloat(newData.repasse_vt) || 0);
        newData.desconto_vt = 0;
      }
      
      // Always recalculate when any relevant field changes
      return calculateEmployeeFields(newData);
    });
  };

  // Function to calculate employee fields based on regime (mirrors backend logic)
  const calculateEmployeeFields = (data) => {
    const regime = data.regime || '';
    const salario = parseFloat(data.salario) || 0;
    const vt = parseFloat(data.vt) || 0;
    
    // Create a copy to avoid mutation
    const newData = { ...data };
    
    if (regime === 'PJ') {
      // For PJ, set specific fields to 0 and calculate 13 NF only if not manually set
      newData.vt = 0;
      newData.desconto_vt = 0;
      newData.vt_custo = 0;
      newData.taxa_vt = 0;
      newData.repasse_vt = 0;
      newData.va = 0;
      newData.vr = 0;
      newData.auxilio_creche = 0;
      newData.segunda_refeicao = 0;
      newData.pernoite = 0;
      newData.med_ocupacional = 'Nenhuma';
      newData.prov_13 = 0;
      newData.prov_ferias = 0;
      newData.fgts = 0;
      newData.inss = 0;
      // Only auto-calculate 13 NF if salary changed and it's not manually edited
      if (!newData.nf_13_manual_edit && salario > 0) {
        newData.nf_13 = newData.possui_decimo_terceiro !== false ? salario / 12 : 0;
      }
    } else if (regime === 'CLT') {
      // For CLT, calculate specific fields and set 13 NF to 0 only if not manually set
      if (!newData.nf_13_manual_edit) {
        newData.nf_13 = 0;
      }
      // Only calculate desconto_vt if VT > 0
      if (vt > 0) {
        newData.desconto_vt = Math.min(vt * 22, salario * 0.06);
      } else {
        newData.desconto_vt = 0;
      }
      newData.prov_13 = salario * 0.0833;
      newData.prov_ferias = salario * 0.1111;
      newData.fgts = salario * 0.08;
    } else if (salario > 0) {
      // If salary is filled but no regime selected, show preview calculations
      // Only calculate desconto_vt if VT > 0
      if (vt > 0) {
        newData.desconto_vt = Math.min(vt * 22, salario * 0.06);
      } else {
        newData.desconto_vt = 0;
      }
      newData.prov_13 = salario * 0.0833;
      newData.prov_ferias = salario * 0.1111;
      newData.fgts = salario * 0.08;
      if (!newData.nf_13_manual_edit) {
        newData.nf_13 = salario / 12;
      }
    }
    
    // Calculate VT CUSTO for both regimes + taxa_vt + repasse_vt
    const taxa_vt = parseFloat(newData.taxa_vt) || 0;
    const repasse_vt = parseFloat(newData.repasse_vt) || 0;
    if (vt > 0) {
      const desconto_vt = parseFloat(newData.desconto_vt) || 0;
      newData.vt_custo = Math.max(0, vt * 22 - desconto_vt) + taxa_vt + repasse_vt;
    } else {
      newData.vt_custo = taxa_vt + repasse_vt;
    }
    
    // Helper function to get numeric value from selection fields
    const getSelectionValue = (fieldName) => {
      const selectedValue = newData[fieldName];
      if (!selectedValue || selectedValue === 'Nenhuma') return 0;
      
      // Find the option value based on selection
      const optionMap = {
        'odonto': 'odontoOptions',
        'seg_vida': 'segVidaOptions', 
        'total_pass': 'totalPassOptions',
        'med_ocupacional': 'medOcupacionalOptions'
      };
      
      const optionKey = optionMap[fieldName];
      if (optionKey && options[optionKey]) {
        const option = options[optionKey].find(opt => opt.name === selectedValue);
        return option ? option.value : 0;
      }
      return 0;
    };
    
    // Calculate TOTAL (sum all numeric fields except VT, DESCONTO VT (-), FGTS)
    const totalFields = [
      'salario', 'acumulo_funcao', 'vt_custo', 'va', 'vr', 'aux_combustivel',
      'hora_extra', 'comissao', 'ajuda_custo',
      'auxilio_creche', 'segunda_refeicao', 'pernoite', 'assist_medica',
      'nf_13', 'prov_13', 'prov_ferias', 'inss'
    ];
    
    const total = totalFields.reduce((sum, field) => {
      const value = parseFloat(newData[field]) || 0;
      return sum + value;
    }, 0);
    
    // Add selection field values to total
    const selectionTotal = getSelectionValue('odonto') + 
                          getSelectionValue('seg_vida') + 
                          getSelectionValue('total_pass') + 
                          getSelectionValue('med_ocupacional');
    
    newData.total = total + selectionTotal;
    
    return newData;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const parseCurrency = (value) => {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    // Remove currency symbols, spaces, and convert comma to dot
    const cleaned = value.toString().replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  };

  const handleCurrencyChange = (field, value) => {
    const numericValue = parseCurrency(value);
    handleInputChange(field, numericValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.empresa || !formData.regime) {
      toast.error('Preencha os campos obrigatórios: Nome, Empresa e Regime');
      return;
    }

    if (formData.admissao && !validateDateBR(formData.admissao, { pastYears: 15, futureYears: 15 })) {
      toast.error('Data de admissão inválida');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditing) {
        await axios.put(`${API}/employees/${id}`, formData);
        toast.success('Funcionário atualizado com sucesso!');
      } else {
        await axios.post(`${API}/employees`, formData);
        toast.success('Funcionário cadastrado com sucesso!');
      }
      
      navigate('/funcionarios');
    } catch (error) {
      console.error('Erro ao salvar funcionário:', error);
      toast.error('Erro ao salvar funcionário');
    } finally {
      setLoading(false);
    }
  };

  const isFieldDisabled = (field) => {
    if (formData.regime === 'PJ') {
      return ['vt', 'va', 'vr', 'auxilio_creche', 'segunda_refeicao', 'pernoite', 'med_ocupacional'].includes(field);
    }
    return false;
  };
  
  const isCalculatedField = (field) => {
    return ['desconto_vt', 'vt_custo', 'prov_13', 'prov_ferias', 'fgts', 'total'].includes(field);
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/funcionarios')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">
              {isEditing ? 'Editar Funcionário' : 'Novo Funcionário'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isEditing ? 'Edite as informações do funcionário' : 'Preencha os dados do novo funcionário'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Tabs value={activeTab} onValueChange={(value) => {
          setActiveTab(value);
          if (value === "basic" || value === "contractual") {
            loadContractualData();
          }
        }} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            {isEditing && <TabsTrigger value="profile">Perfil</TabsTrigger>}
            <TabsTrigger value="basic">Dados Pessoais</TabsTrigger>
            <TabsTrigger value="contractual">Contratuais</TabsTrigger>
            <TabsTrigger value="financial">Financeiros</TabsTrigger>
            <TabsTrigger value="benefits">Benefícios</TabsTrigger>
          </TabsList>

          {/* Profile Tab - Only show when editing */}
          {isEditing && (
            <TabsContent value="profile">
              <div className="space-y-6">
                {/* Header with Photo and Basic Info */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        {formData.foto ? (
                          <img
                            src={formData.foto}
                            alt={formData.nome}
                            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-gray-200 border-4 border-white shadow-lg flex items-center justify-center">
                            <User className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                        
                        {/* DISC Badge */}
                        {profileData.disc && profileData.disc.length > 0 && (
                          <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 border-white shadow-lg ${getDISCColor(profileData.disc[0].primary_style)}`}>
                            {profileData.disc[0].primary_style}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900">{formData.nome}</h2>
                        <p className="text-gray-600">{formData.cargo}</p>
                        <p className="text-sm text-gray-500">{formData.empresa} - {formData.filiais}</p>
                      </div>
</div>
            </CardContent>
                </Card>

                {/* Progress Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* DISC Assessment */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <Target className="h-5 w-5 text-blue-500" />
                        <span>Avaliação DISC</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Progresso</span>
                          <span className="text-sm font-medium">{getProfileProgress(profileData.disc, 'disc')}%</span>
                        </div>
                        <Progress value={getProfileProgress(profileData.disc, 'disc')} className="h-2" />
                        <div className="text-xs text-gray-500">
                          {profileData.disc && profileData.disc.length > 0 
                            ? `${profileData.disc.length} avaliação(ões) realizadas`
                            : 'Nenhuma avaliação realizada'
                          }
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Performance Evaluation */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <BarChart3 className="h-5 w-5 text-green-500" />
                        <span>Avaliação de Desempenho</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Progresso</span>
                          <span className="text-sm font-medium">{getProfileProgress(profileData.performance, 'performance')}%</span>
                        </div>
                        <Progress value={getProfileProgress(profileData.performance, 'performance')} className="h-2" />
                        <div className="text-xs text-gray-500">
                          {profileData.performance && profileData.performance.length > 0
                            ? `${profileData.performance.length} avaliação(ões) de desempenho`
                            : 'Nenhuma avaliação de desempenho'
                          }
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* PDI */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <TrendingUp className="h-5 w-5 text-purple-500" />
                        <span>PDI</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Progresso</span>
                          <span className="text-sm font-medium">{getProfileProgress(profileData.pdi, 'pdi')}%</span>
                        </div>
                        <Progress value={getProfileProgress(profileData.pdi, 'pdi')} className="h-2" />
                        <div className="text-xs text-gray-500">
                          {profileData.pdi && profileData.pdi.length > 0
                            ? `${profileData.pdi.length} PDI(s) criados`
                            : 'Nenhum PDI criado'
                          }
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 9Box */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <Grid3X3 className="h-5 w-5 text-orange-500" />
                        <span>9Box</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Posicionamento</span>
                          <span className="text-sm font-medium">{getProfileProgress(profileData.ninebox, 'ninebox')}%</span>
                        </div>
                        <Progress value={getProfileProgress(profileData.ninebox, 'ninebox')} className="h-2" />
                        <div className="text-xs text-gray-500">
                          {profileData.ninebox && profileData.ninebox.position
                            ? `Posicionado na matriz 9Box`
                            : 'Não posicionado na matriz 9Box'
                          }
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* LMS */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <GraduationCap className="h-5 w-5 text-indigo-500" />
                        <span>LMS</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Cursos Concluídos</span>
                          <span className="text-sm font-medium">{getProfileProgress(profileData.lms, 'lms')}%</span>
                        </div>
                        <Progress value={getProfileProgress(profileData.lms, 'lms')} className="h-2" />
                        <div className="text-xs text-gray-500">
                          {profileData.lms && profileData.lms.length > 0
                            ? `${profileData.lms.filter(c => c.progress_percentage >= 100).length} de ${profileData.lms.length} cursos concluídos`
                            : 'Nenhum curso matriculado'
                          }
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Overall Performance Summary */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center space-x-2 text-lg">
                        <Award className="h-5 w-5 text-yellow-500" />
                        <span>Performance Geral</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Score Geral</span>
                          <span className="text-sm font-medium">
                            {Math.round((
                              getProfileProgress(profileData.disc, 'disc') +
                              getProfileProgress(profileData.performance, 'performance') +
                              getProfileProgress(profileData.pdi, 'pdi') +
                              getProfileProgress(profileData.ninebox, 'ninebox') +
                              getProfileProgress(profileData.lms, 'lms')
                            ) / 5)}%
                          </span>
                        </div>
                        <Progress 
                          value={Math.round((
                            getProfileProgress(profileData.disc, 'disc') +
                            getProfileProgress(profileData.performance, 'performance') +
                            getProfileProgress(profileData.pdi, 'pdi') +
                            getProfileProgress(profileData.ninebox, 'ninebox') +
                            getProfileProgress(profileData.lms, 'lms')
                          ) / 5)} 
                          className="h-2" 
                        />
                        <div className="text-xs text-gray-500">
                          Baseado em todas as avaliações e desenvolvimento
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Basic Information Tab */}
          <TabsContent value="basic">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <span>Informações Básicas</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="form-field">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s'-]/g, ''))}
                placeholder="Nome completo do funcionário"
                required
              />
            </div>

            <div className="form-field">
              <Label htmlFor="empresa">Empresa *</Label>
              <Select value={formData.empresa} onValueChange={(value) => handleInputChange('empresa', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContractual ? "Carregando..." : "Selecione a empresa"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingContractual ? (
                    <div className="flex items-center justify-center p-4 text-sm text-gray-500">Carregando...</div>
                  ) : (
                    options.companies.map((company) => (
                      <SelectItem key={company.id} value={company.name}>
                        {company.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="filiais">Filial</Label>
              <Select value={formData.filiais} onValueChange={(value) => handleInputChange('filiais', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContractual ? "Carregando..." : "Selecione a filial"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingContractual ? (
                    <div className="flex items-center justify-center p-4 text-sm text-gray-500">Carregando...</div>
                  ) : (
                    options.branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.name}>
                        {branch.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="cargo">Cargo</Label>
              <Select value={formData.cargo} onValueChange={(value) => handleInputChange('cargo', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContractual ? "Carregando..." : "Selecione o cargo"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingContractual ? (
                    <div className="flex items-center justify-center p-4 text-sm text-gray-500">Carregando...</div>
                  ) : (
                    options.positions.map((position) => (
                      <SelectItem key={position.id} value={position.name}>
                        {position.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="departamento">Departamento</Label>
              <Select value={formData.departamento} onValueChange={(value) => handleInputChange('departamento', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o departamento" />
                </SelectTrigger>
                <SelectContent>
                  {options.departments.map((department) => (
                    <SelectItem key={department.id} value={department.name}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="situacao">Situação</Label>
              <Select value={formData.situacao} onValueChange={(value) => handleInputChange('situacao', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a situação" />
                </SelectTrigger>
                <SelectContent>
                  {options.status.map((status) => (
                    <SelectItem key={status.id} value={status.name}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="admissao">Data de Admissão</Label>
              <Input
                id="admissao"
                type="date"
                value={formData.admissao}
                onChange={(e) => handleInputChange('admissao', e.target.value)}
              />
            </div>

            <div className="form-field">
              <Label htmlFor="regime">Regime *</Label>
              <Select value={formData.regime} onValueChange={(value) => handleInputChange('regime', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o regime" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLT">CLT</SelectItem>
                  <SelectItem value="PJ">PJ</SelectItem>
                  <SelectItem value="ESTAGIARIO">Estagiário</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Financial Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5" />
              <span>Informações Financeiras</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="form-field">
              <Label htmlFor="salario">Salário</Label>
              <CurrencyInput id="salario" value={formData.salario} onChange={v => handleInputChange('salario', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="acumulo_funcao">Acúmulo de Função</Label>
              <CurrencyInput id="acumulo_funcao" value={formData.acumulo_funcao} onChange={v => handleInputChange('acumulo_funcao', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="vt">VT</Label>
              <CurrencyInput id="vt" value={formData.vt} onChange={v => handleInputChange('vt', v)} placeholder="0,00" disabled={isFieldDisabled('vt')} />
            </div>

            <div className="form-field">
              <Label htmlFor="desconto_vt">Desconto VT (-)</Label>
              <CurrencyInput id="desconto_vt" value={formData.desconto_vt} placeholder="0,00" className="bg-gray-50" disabled title="Campo calculado automaticamente: Salário × 6% (CLT)" />
            </div>

            <div className="form-field">
              <Label htmlFor="vt_custo">VT Custo</Label>
              <CurrencyInput id="vt_custo" value={formData.vt_custo} placeholder="0,00" className="bg-gray-50" disabled title="Campo calculado automaticamente: VT - Desconto VT + Taxa + Repasse" />
            </div>

            <div className="form-field">
              <Label htmlFor="taxa_vt">Taxa VT</Label>
              <CurrencyInput id="taxa_vt" value={formData.taxa_vt} onChange={v => handleInputChange('taxa_vt', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="repasse_vt">Repasse VT</Label>
              <CurrencyInput id="repasse_vt" value={formData.repasse_vt} onChange={v => handleInputChange('repasse_vt', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="va">VA</Label>
              <CurrencyInput id="va" value={formData.va} onChange={v => handleInputChange('va', v)} placeholder="0,00" disabled={isFieldDisabled('va')} />
            </div>

            <div className="form-field">
              <Label htmlFor="vr">VR</Label>
              <CurrencyInput id="vr" value={formData.vr} onChange={v => handleInputChange('vr', v)} placeholder="0,00" disabled={isFieldDisabled('vr')} />
            </div>

            <div className="form-field">
              <Label htmlFor="aux_combustivel">Aux. Combustível</Label>
              <CurrencyInput id="aux_combustivel" value={formData.aux_combustivel} onChange={v => handleInputChange('aux_combustivel', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="hora_extra">Hora Extra</Label>
              <CurrencyInput id="hora_extra" value={formData.hora_extra} onChange={v => handleInputChange('hora_extra', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="comissao">Comissão</Label>
              <CurrencyInput id="comissao" value={formData.comissao} onChange={v => handleInputChange('comissao', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="ajuda_custo">Ajuda de Custo</Label>
              <CurrencyInput id="ajuda_custo" value={formData.ajuda_custo} onChange={v => handleInputChange('ajuda_custo', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="auxilio_creche">Auxílio Creche</Label>
              <CurrencyInput id="auxilio_creche" value={formData.auxilio_creche} onChange={v => handleInputChange('auxilio_creche', v)} placeholder="0,00" disabled={isFieldDisabled('auxilio_creche')} />
            </div>

            <div className="form-field">
              <Label htmlFor="segunda_refeicao">2ª Refeição</Label>
              <CurrencyInput id="segunda_refeicao" value={formData.segunda_refeicao} onChange={v => handleInputChange('segunda_refeicao', v)} placeholder="0,00" disabled={isFieldDisabled('segunda_refeicao')} />
            </div>

            <div className="form-field">
              <Label htmlFor="pernoite">Pernoite</Label>
              <CurrencyInput id="pernoite" value={formData.pernoite} onChange={v => handleInputChange('pernoite', v)} placeholder="0,00" disabled={isFieldDisabled('pernoite')} />
            </div>

            <div className="form-field">
              <Label htmlFor="assist_medica">Assist. Médica</Label>
              <CurrencyInput id="assist_medica" value={formData.assist_medica} onChange={v => handleInputChange('assist_medica', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="odonto">Odonto</Label>
              <Select value={formData.odonto} onValueChange={(value) => handleInputChange('odonto', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione opção de odonto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                  {options.odontoOptions.map((option) => (
                    <SelectItem key={option.id} value={option.name}>
                      {option.name} - {formatCurrency(option.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="seg_vida">Seg. Vida</Label>
              <Select value={formData.seg_vida} onValueChange={(value) => handleInputChange('seg_vida', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione opção de seguro de vida" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                  {options.segVidaOptions.map((option) => (
                    <SelectItem key={option.id} value={option.name}>
                      {option.name} - {formatCurrency(option.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="total_pass">Total Pass</Label>
              <Select value={formData.total_pass} onValueChange={(value) => handleInputChange('total_pass', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione opção de Total Pass" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                  {options.totalPassOptions.map((option) => (
                    <SelectItem key={option.id} value={option.name}>
                      {option.name} - {formatCurrency(option.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="nf_13">13 NF</Label>
              <CurrencyInput id="nf_13" value={formData.nf_13} onChange={v => handleInputChange('nf_13', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="med_ocupacional">Med. Ocupacional</Label>
              <Select 
                value={formData.med_ocupacional} 
                onValueChange={(value) => handleInputChange('med_ocupacional', value)}
                disabled={isFieldDisabled('med_ocupacional')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione opção de medicina ocupacional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nenhuma">Nenhuma</SelectItem>
                  {options.medOcupacionalOptions.map((option) => (
                    <SelectItem key={option.id} value={option.name}>
                      {option.name} - {formatCurrency(option.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-field">
              <Label htmlFor="prov_13">Prov. 13º</Label>
              <CurrencyInput id="prov_13" value={formData.prov_13} placeholder="0,00" className="bg-gray-50" disabled title="Campo calculado automaticamente: Salário × 8,33% (apenas para CLT)" />
            </div>

            <div className="form-field">
              <Label htmlFor="prov_ferias">Prov. Férias</Label>
              <CurrencyInput id="prov_ferias" value={formData.prov_ferias} placeholder="0,00" className="bg-gray-50" disabled title="Campo calculado automaticamente: Salário × 11,11% (apenas para CLT)" />
            </div>

            <div className="form-field">
              <Label htmlFor="fgts">FGTS</Label>
              <CurrencyInput id="fgts" value={formData.fgts} placeholder="0,00" className="bg-gray-50" disabled title="Campo calculado automaticamente: Salário × 8% (apenas para CLT)" />
            </div>

            <div className="form-field">
              <Label htmlFor="inss">INSS</Label>
              <CurrencyInput id="inss" value={formData.inss} onChange={v => handleInputChange('inss', v)} placeholder="0,00" />
            </div>

            <div className="form-field">
              <Label htmlFor="total">TOTAL</Label>
              <CurrencyInput id="total" value={formData.total} placeholder="0,00" className="bg-green-50 font-bold text-green-700" disabled title="Campo calculado automaticamente: Soma de todos os campos exceto VT, Desconto VT(-) e FGTS" />
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Contractual Tab */}
      <TabsContent value="contractual">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Briefcase className="h-5 w-5" />
              <span>Informações Contratuais</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="form-field">
              <Label htmlFor="empresa">Empresa</Label>
              <Select value={formData.empresa} onValueChange={(value) => handleInputChange('empresa', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContractual ? "Carregando..." : "Selecione a empresa"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingContractual ? (
                    <div className="flex items-center justify-center p-4 text-sm text-gray-500">Carregando...</div>
                  ) : (
                    options.companies.map((company) => (
                      <SelectItem key={company.id} value={company.name}>
                        {company.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="form-field">
              <Label htmlFor="filiais">Filial</Label>
              <Select value={formData.filiais} onValueChange={(value) => handleInputChange('filiais', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContractual ? "Carregando..." : "Selecione a filial"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingContractual ? (
                    <div className="flex items-center justify-center p-4 text-sm text-gray-500">Carregando...</div>
                  ) : (
                    options.branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.name}>
                        {branch.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="form-field">
              <Label htmlFor="cargo">Cargo</Label>
              <Select value={formData.cargo} onValueChange={(value) => handleInputChange('cargo', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContractual ? "Carregando..." : "Selecione o cargo"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingContractual ? (
                    <div className="flex items-center justify-center p-4 text-sm text-gray-500">Carregando...</div>
                  ) : (
                    options.positions.map((position) => (
                      <SelectItem key={position.id} value={position.name}>
                        {position.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="form-field">
              <Label htmlFor="departamento">Departamento</Label>
              <Select value={formData.departamento} onValueChange={(value) => handleInputChange('departamento', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o departamento" />
                </SelectTrigger>
                <SelectContent>
                  {options.departments.map((department) => (
                    <SelectItem key={department.id} value={department.name}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="form-field">
              <Label htmlFor="tipo_contrato">Tipo de Contrato</Label>
              <Select value={formData.tipo_contrato} onValueChange={(value) => handleInputChange('tipo_contrato', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="hibrido">Híbrido</SelectItem>
                  <SelectItem value="home_office">Home Office</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.regime === 'PJ' && (
              <div className="form-field flex items-center space-x-2 pt-6">
                <Checkbox
                  id="possui_decimo_terceiro"
                  checked={formData.possui_decimo_terceiro}
                  onCheckedChange={(checked) => handleInputChange('possui_decimo_terceiro', checked)}
                />
                <Label htmlFor="possui_decimo_terceiro">Possui Décimo Terceiro</Label>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Financial Tab */}
      <TabsContent value="financial">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5" />
              <span>Informações Financeiras</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Financial fields will be moved here */}
            <p className="text-gray-500 col-span-full">Campos financeiros serão organizados aqui...</p>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Benefits Tab */}
      <TabsContent value="benefits">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Award className="h-5 w-5" />
              <span>Benefícios</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Benefits fields will be moved here */}
            <p className="text-gray-500 col-span-full">Campos de benefícios serão organizados aqui...</p>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>

        {/* PIN Section */}
        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Smartphone className="h-5 w-5 text-emerald-600" />
                <span>Acesso ao Ponto-App</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pinData.loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${pinData.tem_pin ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <Key className={`h-5 w-5 ${pinData.tem_pin ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">PIN de Acesso</p>
                      <p className="text-xs text-slate-500">
                        {pinData.tem_pin
                          ? (pinData.pin ? 'PIN disponível para visualização' : 'PIN legado cadastrado')
                          : 'Nenhum PIN cadastrado'}
                      </p>
                    </div>
                  </div>
                  {pinData.tem_pin && (
                    <div className="text-right mr-4">
                      {pinData.pin ? (
                        <div className="font-mono text-lg font-bold tracking-widest text-slate-700">{pinData.pin}</div>
                      ) : (
                        <div className="text-xs font-medium text-amber-700">Regenerar para visualizar</div>
                      )}
                    </div>
                  )}
                  {pinData.tem_pin ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetPin}
                      disabled={pinData.generating}
                      className="text-amber-600 border-amber-300"
                    >
                      {pinData.generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Key className="h-4 w-4 mr-1" />}
                      Redefinir
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      onClick={gerarPin}
                      disabled={pinData.generating}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {pinData.generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Key className="h-4 w-4 mr-1" />}
                      Gerar PIN
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* PIN Dialog */}
        <Dialog open={!!pinData.showDialog} onOpenChange={(o) => !o && setPinData(prev => ({ ...prev, showDialog: null }))}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>PIN Gerado</DialogTitle>
              <DialogDescription>
                Entregue este PIN ao colaborador. A visualização posterior fica restrita ao Grid.
              </DialogDescription>
            </DialogHeader>
            {pinData.showDialog && (
              <div className="text-center py-6">
                <p className="text-sm text-slate-500 mb-2">{pinData.showDialog.nome}</p>
                <div className="text-4xl font-mono font-bold tracking-widest text-primary bg-slate-100 rounded-lg py-4 px-6 inline-block">
                  {pinData.showDialog.pin}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setPinData(prev => ({ ...prev, showDialog: null }))} className="w-full">
                Anotei o PIN
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action buttons */}
        <div className="flex items-center justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/funcionarios')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            {loading ? (
              <div className="loading-spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{isEditing ? 'Atualizar' : 'Salvar'}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeForm;
